import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {ActivationChannel, ContactStatus} from '../../domain/entities/Activation.ts'
import {CrmActivationDetail, CrmCommand, HistoryPage, NextAction, NotesFilter, PipelineId, PreparedMessage, TeamMember} from '../../domain/entities/Pipeline.ts'
import {Touchpoint} from '../../domain/entities/Activation.ts'
import {useCrmHistory, useCrmMutations, useCrmNotes} from '../../hooks/usePipelines.ts'
import {crmErrorMessage, FIELD_CLASS, historyText, newCommandId, relativeDue, SECTION_LABEL, TONE, touchLabel, useDateFormatters} from './pipelineView.ts'
import {Avatar, CopyButton, NextActionWho, Spinner, StepDots, Tag} from './PipelineParts.tsx'

const SECTION = 'px-6 py-5 border-b border-neutralBlack-100'
const GHOST_BTN = 'h-8 px-3 rounded-lg border border-neutralBlack-200 bg-white text-[12.5px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out disabled:cursor-not-allowed disabled:text-neutralBlack-500'
const PRIMARY_BTN = 'h-9 px-4 rounded-[10px] bg-neutralBlack-900 text-white text-[13px] font-semibold hover:bg-neutralBlack-700 transition-colors duration-200 ease-out disabled:bg-neutralBlack-100 disabled:text-neutralBlack-600 disabled:cursor-not-allowed'

type SendCommand<C = CrmCommand> = (command: C, successKey: string) => Promise<unknown>
type AssignCommand = Extract<CrmCommand, {type: 'ASSIGN'}>
type UpdateContactCommand = Extract<CrmCommand, {type: 'UPDATE_CONTACT'}>

// What the Next touch block reads from either pipeline's detail.
export type NextTouchDetail = {
    crm: {nextAction: NextAction | null}
    nextTouch: {
        touchpoint: Touchpoint
        position: {index: number; total: number} | null
        preparedMessage: PreparedMessage | null
    } | null
    allowedCommands: string[]
}

// ── Next touch ────────────────────────────────────────────────────────────

export const NextTouchSection: React.FC<{
    detail: NextTouchDetail
    team: TeamMember[]
    channel: ActivationChannel | undefined
    onChannelChange: (channel: ActivationChannel) => void
    onMarkDone: () => void
    onAddFollowUp: () => void
    onEditContact: () => void
    sendCommand: SendCommand<AssignCommand>
    isFetching: boolean
    // How a WAIT's date reads: the code deadline in Activation, the end of
    // On hold in Conversion.
    waitDateLabel?: (date: string) => string
    // Pipeline-specific buttons under the touch (Conversion: Mark draft as sent).
    extraActions?: React.ReactNode
}> = ({detail, team, channel, onChannelChange, onMarkDone, onAddFollowUp, onEditContact, sendCommand, isFetching, waitDateLabel, extraActions}) => {
    const {t} = useTranslation('dashboard')
    const {formatDateTime} = useDateFormatters()
    const {nextAction} = detail.crm
    const touch = detail.nextTouch?.touchpoint
    const position = detail.nextTouch?.position ?? null
    const message = detail.nextTouch?.preparedMessage ?? null
    const allowed = (type: string) => !!detail.allowedCommands?.includes(type)
    const isHuman = touch?.type === 'HUMAN'
    const isFixContact = touch?.key === 'FIX_CONTACT'
    const dueAt = nextAction?.dueAt ?? touch?.dueAt ?? touch?.scheduledAt ?? null
    const due = relativeDue(dueAt, t)
    const activeChannel = channel ?? message?.channel

    return (
        <section className={SECTION} aria-labelledby="pl-next-title">
            <div className="rounded-[14px] border border-neutralBlack-200 bg-neutralBlack-50 p-4">
                <h3 id="pl-next-title" className={SECTION_LABEL}>{t('pipelines.panel.nextTouch')}</h3>
                {!nextAction ? (
                    <p className="mt-2.5 text-[13px] text-neutralBlack-700">{t('pipelines.panel.noNextTouch')}</p>
                ) : (
                    <>
                        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                            <NextActionWho nextAction={nextAction} team={team}/>
                            <span className={`text-[15px] font-semibold ${nextAction.type === 'WAIT' ? 'text-[#8A5A00]' : 'text-neutralBlack-900'}`}>
                                {touchLabel(touch?.key ?? nextAction.key, nextAction.text, t)}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap text-[12.5px] text-neutralBlack-700">
                            {(dueAt != null || nextAction.type !== 'WAIT') && (
                                <span>{nextAction.type === 'WAIT'
                                    ? (waitDateLabel ?? ((date: string) => t('pipelines.panel.deadline', {date})))(formatDateTime(dueAt))
                                    : formatDateTime(dueAt)}</span>
                            )}
                            {due && nextAction.type !== 'WAIT' && (
                                <span className={due.overdue ? 'text-[#B42318] font-semibold' : 'text-neutralBlack-600'}>· {due.text}</span>
                            )}
                            {touch?.channel && !isHuman && <span>· {t('pipelines.panel.via', {channel: t(`pipelines.channel.${touch.channel}`)})}</span>}
                        </div>
                        {position && <div className="mt-2"><StepDots step={position}/></div>}
                    </>
                )}

                {isHuman && isFixContact && (
                    <div className="mt-3.5 flex flex-col gap-2.5">
                        <p className="text-[12.5px] leading-[1.5] text-neutralBlack-700">{t('pipelines.panel.fixContactHint')}</p>
                        {allowed('UPDATE_CONTACT') && (
                            <div><button type="button" onClick={onEditContact} className={GHOST_BTN}>{t('pipelines.panel.editContact')}</button></div>
                        )}
                    </div>
                )}

                {isHuman && message && (
                    <div className="mt-3.5">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[12px] text-neutralBlack-600">{t('pipelines.panel.preparedMessage')}</span>
                            {message.channels?.length > 1 && (
                                <span role="radiogroup" aria-label={t('pipelines.form.channel')} className="inline-flex rounded-lg border border-neutralBlack-200 overflow-hidden">
                                    {message.channels.map((c) => (
                                        <button key={c} type="button" role="radio" aria-checked={c === activeChannel}
                                                onClick={() => onChannelChange(c)}
                                                className={`h-7 px-2.5 text-[11.5px] font-semibold transition-colors duration-200 ease-out ${c === activeChannel ? 'bg-neutralBlack-900 text-white' : 'bg-white text-neutralBlack-700 hover:bg-neutralBlack-100'}`}>
                                            {t(`pipelines.channel.${c}`)}
                                        </button>
                                    ))}
                                </span>
                            )}
                            {isFetching && <span className="text-neutralBlack-600"><Spinner/></span>}
                        </div>
                        {message.to && (
                            <div className="mt-2 flex items-center gap-1 text-[12.5px] text-neutralBlack-700">
                                <span>{t('pipelines.panel.to', {to: message.to})}</span>
                                <CopyButton text={message.to} label={t('pipelines.panel.copyTo')}/>
                            </div>
                        )}
                        {message.subject && (
                            <div className="mt-1 flex items-center gap-1 text-[12.5px] text-neutralBlack-700">
                                <span className="font-medium">{t('pipelines.panel.subject', {subject: message.subject})}</span>
                                <CopyButton text={message.subject} label={t('pipelines.panel.copySubject')}/>
                            </div>
                        )}
                        <div className="mt-2 rounded-[10px] border border-neutralBlack-200 bg-white px-3.5 py-3 text-[13px] leading-[1.55] text-neutralBlack-900 whitespace-pre-wrap select-text">
                            {message.text}
                        </div>
                        {activeChannel === 'WHATSAPP' && message.whatsappTemplate && (
                            <div className="mt-2 rounded-[10px] border border-neutralBlack-200 bg-white px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-neutralBlack-700">
                                <div className="flex items-center gap-1">
                                    <span>{t('pipelines.panel.whatsappTemplate')}</span>
                                    <span className="font-semibold text-neutralBlack-900">{message.whatsappTemplate}</span>
                                    <CopyButton text={message.whatsappTemplate} label={t('pipelines.panel.copyTemplate')}/>
                                </div>
                                <p className="text-neutralBlack-600">{t('pipelines.panel.whatsappTemplateHint')}</p>
                                {message.whatsappBody && Object.keys(message.whatsappBody).length > 0 && (
                                    <dl className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                                        {Object.entries(message.whatsappBody)
                                            .sort(([a], [b]) => Number(a) - Number(b))
                                            .map(([position, value]) => (
                                                <div key={position} className="flex gap-1">
                                                    <dt className="text-neutralBlack-600">{`{{${position}}}`}</dt>
                                                    <dd className="text-neutralBlack-900">{value}</dd>
                                                </div>
                                            ))}
                                    </dl>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {isHuman && !isFixContact && (
                    <div className="mt-3.5 flex items-center gap-2 flex-wrap">
                        {message && (
                            <CopyButton text={message.text} label={t('pipelines.panel.copyMessage')} className={GHOST_BTN + ' h-9 rounded-[10px]'}/>
                        )}
                        {allowed('TOUCHPOINT_DONE') && (
                            <button type="button" onClick={onMarkDone} className={PRIMARY_BTN}>{t('pipelines.panel.markDone')}</button>
                        )}
                    </div>
                )}

                {isHuman && allowed('ASSIGN') && team.length > 0 && (
                    <label className="mt-3 flex items-center gap-2 text-[12.5px] text-neutralBlack-700">
                        {t('pipelines.panel.assignee')}
                        <select value={touch?.assignee ?? ''}
                                onChange={(e) => sendCommand({
                                    commandId: newCommandId(), type: 'ASSIGN',
                                    data: {touchpointId: touch?.id, assignee: e.target.value || null},
                                }, 'pipelines.feedback.assigned').catch(() => undefined)}
                                className="h-8 pl-2 pr-7 rounded-lg border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-900">
                            <option value="">{t('pipelines.unassigned')}</option>
                            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </label>
                )}

                {extraActions && <div className="mt-3.5 flex items-center gap-2 flex-wrap">{extraActions}</div>}

                {allowed('ADD_FOLLOW_UP') && (
                    <div className="mt-3">
                        <button type="button" onClick={onAddFollowUp} className={GHOST_BTN}>{t('pipelines.panel.addFollowUp')}</button>
                    </div>
                )}
            </div>
        </section>
    )
}

// ── Contact ───────────────────────────────────────────────────────────────

const ContactRow: React.FC<{
    label: string
    value: string | null | undefined
    status?: ContactStatus
    inputType: 'email' | 'tel'
    canEdit: boolean
    editing: boolean
    onEditingChange: (editing: boolean) => void
    onSave: (value: string) => Promise<unknown>
}> = ({label, value, status, inputType, canEdit, editing, onEditingChange, onSave}) => {
    const {t} = useTranslation('dashboard')
    const [draft, setDraft] = useState(value ?? '')
    const [busy, setBusy] = useState(false)
    useEffect(() => { if (editing) setDraft(value ?? '') }, [editing, value])
    const inputId = `pl-contact-${inputType}`

    const save = () => {
        if (busy) return
        setBusy(true)
        onSave(draft.trim()).then(() => onEditingChange(false)).catch(() => undefined).finally(() => setBusy(false))
    }

    return (
        <div className="flex items-center gap-2.5 min-h-[34px]">
            {editing ? (
                <label htmlFor={inputId} className="w-[72px] flex-none text-[12.5px] text-neutralBlack-600">{label}</label>
            ) : (
                <span className="w-[72px] flex-none text-[12.5px] text-neutralBlack-600">{label}</span>
            )}
            {editing ? (
                <form className="flex-1 flex items-center gap-2 min-w-0" onSubmit={(e) => {e.preventDefault(); save()}}>
                    <input id={inputId} type={inputType} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
                           onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onEditingChange(false) } }}
                           className={`${FIELD_CLASS} h-8 flex-1 min-w-0`}/>
                    <button type="submit" disabled={busy} className="h-8 px-3 rounded-lg bg-neutralBlack-900 text-white text-[12.5px] font-semibold hover:bg-neutralBlack-700 transition-colors duration-200 ease-out">
                        {busy ? <Spinner/> : t('pipelines.save')}
                    </button>
                    <button type="button" onClick={() => onEditingChange(false)} className="h-8 px-1.5 text-[12.5px] text-neutralBlack-700 hover:text-neutralBlack-900">
                        {t('cancel')}
                    </button>
                </form>
            ) : (
                <>
                    <span className="flex-1 min-w-0 flex items-center gap-2">
                        {value ? <span className="text-[13px] text-neutralBlack-900 truncate">{value}</span>
                            : <Tag tone="amber">{t('pipelines.panel.missing')}</Tag>}
                        {value && status && status !== 'OK' && <Tag tone="red">{t(`pipelines.contactStatus.${status}`)}</Tag>}
                    </span>
                    <CopyButton text={value} label={t('pipelines.panel.copyValue', {label})}/>
                    {canEdit && (
                        <button type="button" onClick={() => onEditingChange(true)} aria-label={t('pipelines.panel.editValue', {label})}
                                className="h-7 px-2 rounded-md text-[12px] font-medium text-neutralBlack-700 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                            {t('pipelines.edit')}
                        </button>
                    )}
                </>
            )}
        </div>
    )
}

export const ContactSection: React.FC<{
    traveler: {
        email?: string | null
        phone?: string | null
        preferredChannel?: ActivationChannel | null
        contactStatus?: ContactStatus
        phoneStatus?: ContactStatus
    }
    contactFailedChannel?: ActivationChannel | null
    canEdit: boolean
    editRequest: number
    sendCommand: SendCommand<UpdateContactCommand>
    title?: string
}> = ({traveler, contactFailedChannel, canEdit, editRequest, sendCommand, title}) => {
    const {t} = useTranslation('dashboard')
    const [editing, setEditing] = useState<'email' | 'phone' | null>(null)
    // "Edit contact" from the Fix failed contact task opens the failed field.
    useEffect(() => {
        if (editRequest > 0) setEditing(contactFailedChannel && contactFailedChannel !== 'EMAIL' ? 'phone' : 'email')
    }, [editRequest, contactFailedChannel])

    const update = (data: UpdateContactCommand['data']) =>
        sendCommand({commandId: newCommandId(), type: 'UPDATE_CONTACT', data}, 'pipelines.feedback.contactSaved')

    return (
        <section className={SECTION} aria-labelledby="pl-contact-title">
            <h3 id="pl-contact-title" className={`${SECTION_LABEL} mb-3`}>{title ?? t('pipelines.panel.contact')}</h3>
            <ContactRow label={t('pipelines.panel.email')} value={traveler.email} status={traveler.contactStatus} inputType="email"
                        canEdit={canEdit} editing={editing === 'email'} onEditingChange={(on) => setEditing(on ? 'email' : null)}
                        onSave={(email) => update({email})}/>
            <ContactRow label={t('pipelines.panel.phone')} value={traveler.phone} status={traveler.phoneStatus} inputType="tel"
                        canEdit={canEdit} editing={editing === 'phone'} onEditingChange={(on) => setEditing(on ? 'phone' : null)}
                        onSave={(phone) => update({phone})}/>
            <div className="flex items-center gap-2.5 min-h-[34px] mt-1">
                <label htmlFor="pl-pref" className="w-[72px] flex-none text-[12.5px] text-neutralBlack-600">{t('pipelines.panel.preferred')}</label>
                {canEdit ? (
                    <select id="pl-pref" value={traveler.preferredChannel ?? ''}
                            onChange={(e) => update({preferredChannel: (e.target.value || '') as ActivationChannel | ''}).catch(() => undefined)}
                            className="h-8 pl-2 pr-7 rounded-lg border border-neutralBlack-200 bg-white text-[12.5px] text-neutralBlack-900">
                        <option value="">{t('pipelines.panel.preferredAuto')}</option>
                        {(['EMAIL', 'SMS', 'WHATSAPP', 'PHONE'] as ActivationChannel[]).map((c) => (
                            <option key={c} value={c}>{t(`pipelines.channel.${c}`)}</option>
                        ))}
                    </select>
                ) : (
                    <span className={`inline-flex items-center h-[22px] px-2.5 rounded-full text-[11.5px] font-semibold ${TONE.teal}`}>
                        {traveler.preferredChannel ? t(`pipelines.channel.${traveler.preferredChannel}`) : t('pipelines.panel.preferredAuto')}
                    </span>
                )}
            </div>
        </section>
    )
}

// ── Code ──────────────────────────────────────────────────────────────────

export const CodeSection: React.FC<{detail: CrmActivationDetail}> = ({detail}) => {
    const {t} = useTranslation('dashboard')
    const {formatDay} = useDateFormatters()
    const {activation, deliverables, agency} = detail
    const days = activation.daysToDeadline
    const row = (label: string, content: React.ReactNode) => (
        <div className="flex items-center gap-2.5 min-h-[32px]">
            <span className="w-[120px] flex-none text-[12.5px] text-neutralBlack-600">{label}</span>
            <span className="flex-1 min-w-0 flex items-center gap-1.5 text-[13px] text-neutralBlack-900">{content}</span>
        </div>
    )
    return (
        <section className={SECTION} aria-labelledby="pl-code-title">
            <h3 id="pl-code-title" className={`${SECTION_LABEL} mb-3`}>{t('pipelines.panel.code')}</h3>
            {row(t('pipelines.panel.codeType'), t(`pipelines.codeType.${activation.codeType}`, {defaultValue: activation.codeType}))}
            {row(t('pipelines.panel.codeValue'), <>
                <span className="font-semibold tabular-nums tracking-[.02em]">{activation.code}</span>
                <CopyButton text={activation.code} label={t('pipelines.panel.copyCode')}/>
            </>)}
            {activation.trip.tripId && row(t('pipelines.panel.tripId'), <>
                <span className="tabular-nums truncate">{activation.trip.tripId}</span>
                <CopyButton text={activation.trip.tripId} label={t('pipelines.panel.copyTripId')}/>
            </>)}
            {row(t('pipelines.panel.expiry'), (
                <span className={days != null && days <= 30 ? 'text-[#B42318] font-medium' : ''}>
                    {formatDay(activation.redemptionDeadline)}
                    {days != null && ` · ${days > 0 ? t('pipelines.due.inDays', {count: days}) : t('pipelines.panel.expired')}`}
                </span>
            ))}
            {(deliverables?.pdfUrl || deliverables?.activationLink) && row(t('pipelines.panel.deliverables'), <>
                {deliverables.pdfUrl && (
                    <a href={deliverables.pdfUrl} target="_blank" rel="noopener noreferrer"
                       className="h-7 px-2.5 rounded-lg border border-neutralBlack-200 inline-flex items-center text-[12.5px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                        {t('pipelines.panel.openPdf')}<span className="sr-only"> {t('pipelines.newTab')}</span>
                    </a>
                )}
                <CopyButton text={deliverables.activationLink} label={t('pipelines.panel.copyLink')}>{t('pipelines.panel.copyLinkShort')}</CopyButton>
            </>)}
            {row(t('pipelines.panel.optIn'), agency.optInPreTrip ? t('pipelines.yes') : t('pipelines.no'))}
        </section>
    )
}

// ── Notes ─────────────────────────────────────────────────────────────────

const NOTE_FILTERS: NotesFilter[] = ['all', 'notes', 'files', 'pinned']

export const NotesSection: React.FC<{pipeline: PipelineId; id: string; canAdd: boolean}> = ({pipeline, id, canAdd}) => {
    const {t} = useTranslation('dashboard')
    const {formatDateTime} = useDateFormatters()
    const [filter, setFilter] = useState<NotesFilter>('all')
    const [text, setText] = useState('')
    const {notes, isLoadingNotes, notesError} = useCrmNotes(pipeline, id, filter)
    const {addCrmNote, isAddingCrmNote, setCrmNotePinned} = useCrmMutations()

    const add = () => {
        const value = text.trim()
        if (!value || isAddingCrmNote) return
        addCrmNote({pipeline, id, text: value})
            .then(() => setText(''))
            .catch((e) => enqueueSnackbar(crmErrorMessage(e, t), {variant: 'error'}))
    }

    return (
        <section className={SECTION} aria-labelledby="pl-notes-title">
            <h3 id="pl-notes-title" className={`${SECTION_LABEL} mb-3`}>{t('pipelines.panel.notes')}</h3>
            {canAdd && (
                <div className="rounded-xl border border-neutralBlack-200 bg-white px-3 py-2.5">
                    <label htmlFor="pl-note" className="sr-only">{t('pipelines.notes.add')}</label>
                    <textarea id="pl-note" rows={2} value={text} onChange={(e) => setText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); add() } }}
                              placeholder={t('pipelines.notes.placeholder')}
                              className="w-full border-0 outline-none resize-none text-[13px] leading-[1.5] text-neutralBlack-900 placeholder:text-neutralBlack-600"/>
                    <div className="flex items-center justify-end mt-1.5">
                        <button type="button" onClick={add} disabled={!text.trim() || isAddingCrmNote}
                                className="h-8 px-4 rounded-lg bg-neutralBlack-900 text-white text-[12.5px] font-semibold hover:bg-neutralBlack-700 transition-colors duration-200 ease-out disabled:bg-neutralBlack-100 disabled:text-neutralBlack-600">
                            {isAddingCrmNote ? <Spinner/> : t('pipelines.notes.addButton')}
                        </button>
                    </div>
                </div>
            )}
            <div role="group" aria-label={t('pipelines.notes.filter')} className="flex flex-wrap gap-1.5 mt-3.5 mb-3">
                {NOTE_FILTERS.map((f) => (
                    <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}
                            className={`h-7 px-3 rounded-full text-[12px] font-medium transition-colors duration-200 ease-out ${filter === f ? 'bg-neutralBlack-900 text-white' : 'bg-neutralBlack-100 text-neutralBlack-700 hover:bg-neutralBlack-200'}`}>
                        {t(`pipelines.notes.filter.${f}`)}
                    </button>
                ))}
            </div>
            {isLoadingNotes ? (
                <div className="h-14 rounded-xl bg-neutralBlack-50 animate-pulse"/>
            ) : notesError ? (
                <p role="alert" className="text-[12.5px] text-[#B42318]">{t('pipelines.notes.error')}</p>
            ) : notes.length === 0 ? (
                <p className="text-[12.5px] text-neutralBlack-600">{t('pipelines.notes.empty')}</p>
            ) : (
                <ul className="flex flex-col gap-3.5">
                    {notes.map((note) => (
                        <li key={note.id} className="flex gap-2.5">
                            <Avatar name={note.authorName ?? note.authorId} size={22}/>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 min-h-[22px]">
                                    <span className="text-[12.5px] font-semibold text-neutralBlack-900">{note.authorName ?? note.authorId}</span>
                                    <span className="text-[12px] text-neutralBlack-600">· {formatDateTime(note.createdAt)}</span>
                                    <span className="flex-1"/>
                                    <button type="button" aria-pressed={note.pinned}
                                            onClick={() => setCrmNotePinned({pipeline, id, noteId: note.id, pinned: !note.pinned})
                                                .catch((e) => enqueueSnackbar(crmErrorMessage(e, t), {variant: 'error'}))}
                                            className={`h-6 px-2 rounded-md text-[11.5px] font-medium transition-colors duration-200 ease-out ${note.pinned ? `${TONE.teal}` : 'text-neutralBlack-600 hover:bg-neutralBlack-100'}`}>
                                        {note.pinned ? t('pipelines.notes.pinned') : t('pipelines.notes.pin')}
                                    </button>
                                </div>
                                {note.text && <p className="text-[13px] leading-[1.5] text-neutralBlack-900 whitespace-pre-wrap mt-0.5">{note.text}</p>}
                                {note.files?.length > 0 && (
                                    <ul className="flex flex-col gap-1.5 mt-2">
                                        {note.files.map((file) => (
                                            <li key={file.url}>
                                                <a href={file.url} target="_blank" rel="noopener noreferrer"
                                                   className="block px-2.5 py-2 rounded-[10px] border border-neutralBlack-200 text-[12.5px] font-medium text-neutralBlack-900 truncate hover:bg-neutralBlack-50 transition-colors duration-200 ease-out">
                                                    {file.name}<span className="sr-only"> {t('pipelines.newTab')}</span>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}

// ── History ───────────────────────────────────────────────────────────────

export const HistorySection: React.FC<{pipeline: PipelineId; id: string; history: HistoryPage | null | undefined; team: TeamMember[]}> = ({pipeline, id, history, team}) => {
    const {t} = useTranslation('dashboard')
    const {formatDay, formatDateTime} = useDateFormatters()
    const [showAll, setShowAll] = useState(false)
    const {events, isLoadingHistory, historyError, hasMoreHistory, isFetchingMoreHistory, loadMoreHistory} = useCrmHistory(pipeline, id, showAll)
    const latest = history?.items ?? []
    const items = showAll && !isLoadingHistory ? events : latest
    const canSeeAll = !showAll && (!!history?.nextCursor || latest.length >= 5)

    return (
        <section className="px-6 pt-5 pb-7" aria-labelledby="pl-history-title">
            <h3 id="pl-history-title" className={`${SECTION_LABEL} mb-3`}>{t('pipelines.panel.history')}</h3>
            {items.length === 0 ? (
                <p className="text-[12.5px] text-neutralBlack-600">{t('pipelines.history.empty')}</p>
            ) : (
                <ol className="flex flex-col">
                    {items.map((event) => (
                        <li key={event.id} className="flex gap-2.5 py-1.5 items-start">
                            <span aria-hidden="true" className="mt-[7px] w-1.5 h-1.5 rounded-full bg-neutralBlack-500 flex-none"/>
                            <span className="flex-1 min-w-0 text-[13px] leading-[1.45] text-neutralBlack-900">
                                {historyText(event, t, formatDay, team)}
                                {event.actor !== 'system' && event.actorName && (
                                    <span className="text-neutralBlack-600"> · {event.actorName}</span>
                                )}
                            </span>
                            <span className="flex-none text-[12px] text-neutralBlack-600 whitespace-nowrap mt-px">{formatDateTime(event.at)}</span>
                        </li>
                    ))}
                </ol>
            )}
            {showAll && isLoadingHistory && <div className="mt-2 text-neutralBlack-600"><Spinner/></div>}
            {showAll && historyError && <p role="alert" className="mt-2 text-[12.5px] text-[#B42318]">{t('pipelines.history.error')}</p>}
            {canSeeAll && (
                <button type="button" onClick={() => setShowAll(true)}
                        className="mt-2 text-[12.5px] font-medium text-neutralBlack-900 underline underline-offset-2 hover:text-neutralBlack-600 transition-colors duration-200 ease-out">
                    {t('pipelines.history.seeAll')}
                </button>
            )}
            {showAll && hasMoreHistory && (
                <button type="button" onClick={() => loadMoreHistory()} disabled={isFetchingMoreHistory}
                        className="mt-2 text-[12.5px] font-medium text-neutralBlack-900 underline underline-offset-2 hover:text-neutralBlack-600 transition-colors duration-200 ease-out">
                    {isFetchingMoreHistory ? t('pipelines.loading') : t('pipelines.history.loadMore')}
                </button>
            )}
        </section>
    )
}
